import { appstr } from "../utils/appstr";
import { ConvertToDate } from "../utils/ConvertToDate";
import { enumCellObjectSubtypes, enumFieldType, themeModeSettingsArray } from "../constants";
import { MapComparison } from "../utils/MapComparison";
import { QXMLObject } from "./QXMLObject";
import {
  FormatFieldValue,
  isBlockedField,
  getBlockedFieldDisplayValue
} from "../utils/FormatFieldValue.js";
import { escapeRegExp } from "../utils/escapeRegExp.js";
import { isValidDate } from "../utils/isValidDate.js";
import { GetFieldLabel } from "../utils/GetFieldLabel";
import { PositionFilterCondition } from "../utils/PositionFilterCondition";
import {
  convertDateRangeToTimestamps,
  convertDynamicDateToTimestamp,
  validRange
} from "../utils/DateRuleUtils";

/**
 * @typedef {import('./QObjDBFieldDef').QObjDBFieldDef} QObjDBFieldDef
 * @typedef {import('./QObjDBObject').QObjDBObject} QObjDBObject
 * @typedef {import('./QXDoc').QXDoc} QXDoc
 */

export const enumRulePairLogicCondition = {
  and: "$and",
  or: "$or"
};

export const enumRuleErrorType = {
  None: "None",
  FieldEmpty: "FieldEmpty",
  ComparisonEmpty: "ComparisonEmpty",
  ValueEmpty: "ValueEmpty",
  BothValuesEmpty: "BothValuesEmpty",
  InvalidDateRange: "InvalidDateRange",
  InvalidNumericRange: "InvalidNumericRange"
};

export class QRulePair extends QXMLObject {
  constructor() {
    super();
    this.fFieldName = null;
    this.fFieldID = null;
    this.fFieldClassName = null;
    /**
     * @private
     * @type {(null | string)}
     */
    this.fComparison = null;
    /**
     * @private
     * @type {(null | string | string[] | number | number[])}
     */
    this.fValue = null;
    this.fCondition = enumRulePairLogicCondition.or;
  }

  DoInit() {
    this.SetXMLElementName("pair");
  }

  InitRulePair(fieldName, fieldID, fieldClassName, comparison, value, fUID) {
    this.fFieldName = fieldName;
    this.fFieldID = fieldID;
    this.fFieldClassName = fieldClassName;
    this.fComparison = comparison;
    this.fValue = value;
    this.fUID = fUID;
    this.DoInit();
  }

  Marshall() {
    const fieldName = this.GetFieldName();
    const comparison = this.GetComparison();
    const value = this.GetValue();

    const expression = {};
    switch (comparison) {
      case "equals":
        expression.$eq = value;
        break;
      case "notequal":
        expression.$ne = value;
        break;
      case "startswith":
        expression.$regex = `^${value}`;
        break;
      case "contains": {
        let re = value;
        try {
          new RegExp(value);
        } catch {
          re = escapeRegExp(value);
        }
        expression.$regex = re;
        break;
      }
      case "doesnotcontain": {
        let re = value;
        try {
          new RegExp(value);
        } catch {
          re = escapeRegExp(value);
        }
        expression.$regex = `^((?!${re}).)*$`;
        break;
      }
      case "isempty":
        expression.$eq = "";
        break;
      case "isnotempty":
        expression.$ne = "";
        break;
      case "greater":
        expression.$gt = value;
        break;
      case "less":
        expression.$lt = value;
        break;
      case "greaterequal":
        expression.$gte = value;
        break;
      case "lessequal":
        expression.$lte = value;
        break;
      case "notinlist":
        expression.$nin = value;
        break;
      case "inlist":
        expression.$in = value;
        break;
    }

    if (expression.$regex) {
      // Regex options are ignored in qrecordfilter and assumed to be this constant
      expression.$options = "i";
    }

    return { [fieldName]: expression };
  }

  /**
   * @param {string} fieldName
   * @param {string} fieldID
   * @param {string} fieldClassName
   * @returns {}
   */
  UpdateFieldName(fieldName, fieldID, fieldClassName) {
    this.fFieldName = fieldName;
    this.fFieldID = fieldID;
    this.fFieldClassName = fieldClassName;
  }

  /**
   * @returns {(string | null)}
   */
  GetFieldName() {
    return this.fFieldName;
  }

  /**
   * @returns {(string | null)}
   */
  GetFieldID() {
    return this.fFieldID;
  }

  /**
   * @returns {(string)}
   */
  GetFieldClassName() {
    return this.fFieldClassName ?? "Box";
  }

  UpdateComparison(comparison) {
    this.fComparison = comparison;
  }

  GetComparison() {
    return this.fComparison;
  }

  UpdateValue(value) {
    this.fValue = value;
  }

  IsFieldToFieldComparison() {
    if (!this.fValue) {
      return false;
    }

    // Comparison to constant value
    if (typeof this.fValue === "string" || isValidDate(this.fValue)) {
      return false;
    }

    // Comparison to list of constants
    if (Array.isArray(this.fValue)) {
      return false;
    }

    if (this.fComparison == "daterange") {
      return false;
    }

    return true;
  }

  GetValue() {
    return this.fValue;
  }

  FormatValue(value, fieldDef) {
    if (!value) {
      return value;
    }

    return FormatFieldValue(value, fieldDef);
  }

  /**
   * @param {QXDoc | null} [docRef]
   * @returns {string}
   */
  GetValueDisplayString(docRef) {
    if (this.IsFieldToFieldComparison()) {
      const comparisonFieldDef = this.GetComparisonFieldDef(docRef);
      if (comparisonFieldDef) {
        return GetFieldLabel(comparisonFieldDef, true);
      }

      const { fieldName } = this.fValue;
      return fieldName;
    }

    const fieldDef = this.GetReferenceFieldDef(docRef);

    let value = this.fValue;
    if (Array.isArray(value) && this.fComparison !== "range") {
      return value.map(v => this.FormatValue(v, fieldDef)).join(", ");
    }

    if (this.fComparison === "daterange" && typeof value === "object" && value !== null) {
      // To do - We might want to refine how we show the range.
      const from = `${appstr(value.from.dateType).toLowerCase()}`.replace(
        "[n]",
        value.from.value ?? "[n]"
      );

      if (value.to.dateType === null) {
        return from;
      } else {
        const to = `${appstr(value.to.dateType).toLowerCase()}`.replace(
          "[n]",
          value.to.value ?? "[n]"
        );

        return `${from} - ${to}`;
      }
    }

    if (this.fComparison === "range" && Array.isArray(value) && value.length === 2) {
      return value.map(v => this.FormatValue(v, fieldDef)).join(" - ");
    }

    return this.FormatValue(value, fieldDef);
  }

  CompareObjectList(docRef, comparison, fieldValue, matchValue) {
    var gotMatch = false;
    try {
      matchValue = JSON.parse(matchValue);
    } catch (err) {
      matchValue = String(matchValue);
    }

    switch (comparison) {
      case "isempty":
        gotMatch = fieldValue.length === 0;
        break;
      case "isnotempty":
        gotMatch = fieldValue.length > 0;
        break;
      default:
        // Other comparisons should be evaluated with respect to a specific property on the objects
        if (Array.isArray(fieldValue)) {
          gotMatch = fieldValue.some(obj => {
            let fieldKey = "Name";
            if (docRef.IsPositionDocument()) {
              fieldKey = "Title";
            }
            const objectValue = obj[fieldKey];
            return this.CompareStr(comparison, objectValue, matchValue);
          });
        }
        break;
    }
    return gotMatch;
  }

  CompareStr(comparison, fieldValue, matchValue) {
    fieldValue = fieldValue ?? "";
    matchValue = matchValue ?? "";

    const v = fieldValue.toString().toLowerCase();
    let mv = matchValue;

    if (Array.isArray(mv)) {
      mv = mv.map(value => `${value}`.toLowerCase());
    } else {
      mv = mv.toString().toLowerCase();
    }

    const vN = parseFloat(v);
    const mvN = parseFloat(mv);

    let theList;
    switch (comparison) {
      case "equals":
        return v == mv;
      case "notequal":
        return v != mv;
      case "startswith":
        return v.slice(0, mv.length) == mv;
      case "contains": {
        try {
          return v.search(mv) !== -1;
        } catch {
          return v.includes(mv);
        }
      }
      case "doesnotcontain": {
        try {
          return v.search(mv) === -1;
        } catch {
          return !v.includes(mv);
        }
      }
      case "isempty":
        return v.length == 0;
      case "isnotempty":
        return v.length > 0;
      case "greater":
        return vN > mvN;
      case "less":
        return vN < mvN;
      case "greaterequal":
        return vN >= mvN;
      case "lessequal":
        return vN <= mvN;
      case "notinlist":
        if (Array.isArray(mv)) {
          return !mv.includes(v);
        }

        theList = [];
        if (matchValue != null)
          // there are cases where 'matchValue' can have undefined or NaN value
          theList = mv.split(",");
        // Only match identical strings
        for (const item of theList) {
          if (v === item) {
            return false;
          }
        }
        return true;
      case "inlist":
        if (Array.isArray(mv)) {
          return mv.includes(v);
        }

        theList = [];
        if (matchValue != null)
          // there are cases where 'matchValue' can have undefined or NaN value
          theList = mv.split(",");
        // Only match identical strings
        for (const item of theList) {
          if (v === item) {
            return true;
          }
        }
        return false;
    }
  }

  CompareNum(comparison, fieldValue, matchValue) {
    var v = Number(fieldValue);
    let mv;

    if (["range", "daterange"].includes(comparison)) {
      mv = [Number(matchValue[0]), Number(matchValue[1])];
    } else {
      mv = Number(matchValue);
    }
    var gotMatch = false;

    switch (comparison) {
      case "equals":
        gotMatch = v == mv;
        break;
      case "notequal":
        gotMatch = v != mv;
        break;
      case "greater":
        gotMatch = v > mv;
        break;
      case "less":
        gotMatch = v < mv;
        break;
      case "greaterequal":
        gotMatch = v >= mv;
        break;
      case "lessequal":
        gotMatch = v <= mv;
        break;
      case "startswith":
      case "contains":
      case "doesnotcontain":
      case "inlist":
      case "notinlist":
        gotMatch = this.CompareStr(comparison, v, matchValue);
        break;
      case "isempty":
      case "isnotempty":
        gotMatch = this.CompareStr(comparison, fieldValue, matchValue);
        break;
      case "range":
      case "daterange":
        if (Array.isArray(mv) && mv.length === 2) {
          const [start, end] = mv;
          gotMatch = start <= v && v <= end;
        }
        break;
    }

    return gotMatch;
  }

  ComparisonRequiresValue(comparison) {
    if (comparison == null) comparison = this.GetComparison();

    return !["isempty", "isnotempty"].includes(comparison);
  }

  /**
   * @param {QObjDBObject} dbObj
   * @returns {QObjDBObject[]}
   */
  ResolveComparisonObject(dbObj) {
    if (!this.IsFieldToFieldComparison()) {
      // This is ONLY valid for field to field comparisons
      return [];
    }

    const { fieldClassName } = this.fValue;
    const parentClass = dbObj.GetParentClassName();

    // If the className matches, the filter should be evaluated with respect to itself
    if (fieldClassName === parentClass) {
      return [dbObj];
    }

    if (parentClass === "Position" && fieldClassName === "Box") {
      return dbObj.GetHolderList() ?? [];
    }

    if (parentClass === "Box" && fieldClassName === "Position") {
      return [dbObj?.GetParentPosition()];
    }

    return [];
  }

  /**
   * Returns the right operator value for the comparison
   * @param {QXDoc} docRef
   * @param {QObjDBFieldDef} fieldDef
   * @returns {(string | number | string[] | number[])}
   */
  ResolveValue(docRef, fieldDef) {
    if (Array.isArray(this.fValue)) {
      const blockedDisplay = getBlockedFieldDisplayValue();
      return this.fValue.map(v =>
        isBlockedField(v) || v === blockedDisplay ? blockedDisplay : v.toString().toLowerCase()
      );
    }

    const fieldType = fieldDef.GetFieldType();
    if (fieldType === enumFieldType.date) {
      if (typeof this.fValue !== "string" && isValidDate(this.fValue)) {
        const date = new Date(this.fValue);
        return date.getTime();
      }

      if (docRef.IsDateVariable(this.fValue)) {
        return docRef.EvaluateVariable(this.fValue);
      }

      if (typeof this.fValue === "object" && this.fComparison === "daterange") {
        return convertDateRangeToTimestamps(this.fValue);
      }

      // assumes then value is either a valid number or string date value representation that can be converted into milliseconds
      return ConvertToDate(this.fValue);
    }

    return this.fValue.toString().toLowerCase();
  }

  /**
   * Evaluates the filter criteria for the given object
   * @param {QObjDBObject} theObj
   * @param {QXDoc} docRef
   * @param {boolean} [resultOnClassMismatch=false]
   * @returns {boolean}
   */
  PairMatch(theObj, docRef, resultOnClassMismatch = false) {
    if (this.GetFieldClassName() !== theObj.GetParentClassName()) {
      return resultOnClassMismatch;
    }

    const fieldDef = this.GetReferenceFieldDef(docRef);
    if (!fieldDef) {
      return false;
    }

    let leftOperand = theObj.GetFieldValue(fieldDef, true);
    if (leftOperand === undefined || leftOperand === null) {
      leftOperand = "";
    }

    // Sanitize blocked sentinel so filters match the display value "(hidden)"
    if (isBlockedField(leftOperand)) {
      leftOperand = getBlockedFieldDisplayValue();
    }

    const comparison = this.GetComparison();

    // When the filter targets blocked values, match on blocked status directly
    // regardless of field type (avoids date/number parsing of the sentinel)
    const blockedDisplay = getBlockedFieldDisplayValue();
    if (isBlockedField(this.fValue) || this.fValue === blockedDisplay) {
      const leftIsBlocked = leftOperand === blockedDisplay;
      switch (comparison) {
        case "equals":
        case "contains":
        case "startswith":
          return leftIsBlocked;
        case "notequal":
        case "doesnotcontain":
          return !leftIsBlocked;
        case "isnotempty":
          return true;
        case "isempty":
          return false;
        default:
          return false;
      }
    }

    // For list comparisons containing blocked items, resolve blocked membership
    // here to avoid PerformComparison's typed parsing (date/number) which
    // rejects the "(hidden)" display string
    if (leftOperand === blockedDisplay && Array.isArray(this.fValue)) {
      const hasBlockedItem = this.fValue.some(v => isBlockedField(v) || v === blockedDisplay);
      if (comparison === "inlist") return hasBlockedItem;
      if (comparison === "notinlist") return !hasBlockedItem;
    }

    if (this.IsFieldToFieldComparison()) {
      return this.PerformFieldComparison(theObj, docRef, fieldDef, leftOperand, comparison);
    }

    let rightOperand = "";
    if (this.fValue) {
      rightOperand = this.ResolveValue(docRef, fieldDef);
    }

    return this.PerformComparison(docRef, fieldDef, leftOperand, comparison, rightOperand);
  }

  /**
   * @param {QObjDBObject} theObj
   * @param {QXDoc} docRef
   * @returns {[boolean, Record<string, boolean>]}
   */
  PositionsPairMatch(theObj, docRef) {
    let result;
    if (!this.GetReferenceFieldDef(docRef)) return PositionFilterCondition.AllFalse(theObj);

    if (this.GetFieldClassName() !== theObj.GetParentClassName()) {
      result = PositionFilterCondition.FromHoldersCondition(theObj, h => this.PairMatch(h, docRef));
    } else {
      const positionResult = this.PairMatch(theObj, docRef, true);
      result = PositionFilterCondition.FromPositionCondition(theObj, positionResult);
    }

    return result;
  }

  /**
   * @param {QXDoc | null} docRef
   * @returns {QObjDBFieldDef | null}
   */
  GetReferenceFieldDef(docRef) {
    if (!docRef) {
      return null;
    }

    const className = this.GetFieldClassName();
    const objDB = docRef.GetObjDB();
    const objClass = objDB.GetClassByName(className);
    if (!objClass || this.fFieldID == null) {
      return null;
    }

    let fieldDef = objClass.GetFieldDefByID(this.fFieldID);
    if (!fieldDef) fieldDef = objClass.GetFieldDefByName(this.fFieldName);
    if (!fieldDef) fieldDef = objClass.GetFieldDefByName(this.fFieldName.replaceAll(" ", "^"));

    return fieldDef ?? null;
  }

  /**
   * @param {QXDoc | null} docRef
   * @returns {QObjDBFieldDef | null}
   */
  GetComparisonFieldDef(docRef) {
    if (!this.IsFieldToFieldComparison()) {
      // This is ONLY valid for field to field comparisons
      return null;
    }

    const { fieldClassName, fieldName, fieldId } = this.fValue;

    const objDB = docRef?.GetObjDB();
    const objClass = objDB?.GetClassByName(fieldClassName);
    if (!objClass) return null;

    let fieldDef = objClass.LookupFieldDef(fieldId);
    if (!fieldDef) fieldDef = objClass.LookupFieldDef(fieldName);

    return fieldDef;
  }

  /**
   * @param {QObjDBObject} baseObj
   * @param {QXDoc} docRef
   * @param {QObjDBFieldDef} fieldDef
   * @param {unknown} leftOperand
   * @param {string} comparison
   * @returns {boolean}
   */
  PerformFieldComparison(baseObj, docRef, fieldDef, leftOperand, comparison) {
    const comparisonObjects = this.ResolveComparisonObject(baseObj);

    // If there are no objects to compare, the filter is false
    if (comparisonObjects.length === 0) {
      return false;
    }

    const comparisonFieldDef = this.GetComparisonFieldDef(docRef);
    // If the comparison field does not exist on this document, the filter is false
    if (!comparisonFieldDef) {
      return false;
    }

    // A filter will only be true if all objects match the comparison
    return comparisonObjects.every(compareObject => {
      const rightOperand = compareObject.GetFieldValue(comparisonFieldDef, true) ?? "";
      return this.PerformComparison(docRef, fieldDef, leftOperand, comparison, rightOperand);
    });
  }

  /**
   * @param {QXDoc} docRef
   * @param {QObjDBFieldDef} fieldDef
   * @param {unknown} leftOperand
   * @param {string} comparison
   * @param {unknown} rightOperand
   * @returns {boolean}
   */
  PerformComparison(docRef, fieldDef, leftOperand, comparison, rightOperand) {
    const fieldType = fieldDef.GetFieldType();

    switch (fieldType) {
      case enumFieldType.number:
        return this.CompareNum(comparison, leftOperand, rightOperand);
      case enumFieldType.text:
        return this.CompareStr(comparison, leftOperand, rightOperand);
      case enumFieldType.date:
        if (isBlockedField(leftOperand) || leftOperand === getBlockedFieldDisplayValue()) {
          return ["isempty", "isnotempty"].includes(comparison)
            ? comparison === "isnotempty"
            : false;
        }
        const isRange = comparison === "daterange";
        if (!isRange) {
          rightOperand =
            rightOperand instanceof Date ? rightOperand.getTime() : ConvertToDate(rightOperand);
        }
        leftOperand =
          leftOperand instanceof Date ? leftOperand.getTime() : ConvertToDate(leftOperand);
        // Blank dates should not match unless checking for empty/not empty
        if (leftOperand == null && !["isempty", "isnotempty"].includes(comparison)) {
          return false;
        }
        return this.CompareNum(comparison, leftOperand, rightOperand);
      case enumFieldType.object:
        const fieldSubtype = fieldDef.GetFieldSubtype();
        switch (fieldSubtype) {
          case enumCellObjectSubtypes.alsoreportsto:
            return this.CompareObjectList(docRef, comparison, leftOperand, rightOperand);
          default:
            console.log("beware");
            break;
        }
        return false;
    }

    return false;
  }

  GetRulePairString(docRef) {
    const fieldDef = this.GetReferenceFieldDef(docRef);
    const fieldLabel = GetFieldLabel(fieldDef, true) || this.GetFieldName();

    let ruleString = "<" + fieldLabel + "> ";

    const cStr = this.GetComparison();
    ruleString += appstr(cStr);

    const ruleValue = this.GetValueDisplayString(docRef);
    if (ruleValue != null && this.ComparisonRequiresValue(cStr)) {
      if (this.IsFieldToFieldComparison()) {
        ruleString += " <" + ruleValue + ">";
      } else {
        ruleString += " '" + ruleValue + "'";
      }
    }
    return ruleString;
  }

  Clone() {
    var c = new QRulePair();
    c.InitRulePair(
      this.fFieldName,
      this.fFieldID,
      this.fFieldClassName,
      this.fComparison,
      this.fValue,
      this.fUID
    );
    c.fCondition = this.fCondition;
    return c;
  }

  DoRulePairsMatch(compareTo) {
    return true;
  }

  RulePairsMatch(compareTo) {
    if (this.fFieldID !== compareTo.fFieldID) {
      if (this.fFieldName !== compareTo.fFieldName) return false;
    }
    if (this.fFieldClassName !== compareTo.fFieldClassName) return false;

    if (this.fComparison !== compareTo.fComparison) return false;
    if (this.fValue !== compareTo.fValue) return false;

    return this.DoRulePairsMatch();
  }

  OutputSubElements(stream) {
    this.OutputElementTriple(
      "expression3",
      this.fFieldName,
      this.fFieldID,
      this.fComparison,
      stream
    );
    this.OutputSimpleElement("exvalue", this.fValue, stream);
  }

  OutputToJSON() {
    return {
      fieldName: this.fFieldName,
      fieldID: this.fFieldID,
      fieldClassName: this.fFieldClassName,
      operator: this.fComparison,
      value: this.fValue,
      uid: this.fUID,
      condition: this.fCondition
    };
  }

  LoadFromJSON(json) {
    this.fFieldName = json["fieldName"];
    this.fFieldID = json["fieldID"];
    this.fFieldClassName = json["fieldClassName"] ?? "Box";
    this.fComparison = json["operator"];
    this.fValue = json["value"];
    this.fUID = json["uid"];
    this.fCondition = json["condition"] ?? enumRulePairLogicCondition.or;
  }

  LoadSubElements(objTree) {
    var valueList = objTree.GetValueListForChild("expression3", false);
    if (valueList) {
      this.fFieldName = valueList[0];
      this.fFieldID = valueList[1];
      this.fComparison = valueList[2];
      var v = objTree.GetValueListForChild("exvalue", false);
      this.fValue = v;
      return;
    }

    // Backward compatibility
    var valueList = objTree.GetValueListForChild("expression2", false);
    if (valueList == null) {
      valueList = objTree.GetValueListForChild("expression", false);
      if (valueList != null) {
        this.fFieldName = valueList[0];
        this.fFieldID = valueList[0];
        this.fComparison = MapComparison(valueList[1]);
        this.fValue = valueList[2];
      }
    } else {
      this.fFieldName = valueList[0];
      this.fFieldID = valueList[1];
      this.fComparison = valueList[2];
      this.fValue = valueList[3];
    }
  }

  IsValid() {
    return this.GetErrorType() === enumRuleErrorType.None;
  }

  GetErrorType() {
    if (this.fFieldID == null || this.fFieldID.length == 0) return enumRuleErrorType.FieldEmpty;

    if (this.fComparison == null || this.fComparison.length == 0)
      return enumRuleErrorType.ComparisonEmpty;

    if (this.fComparison == "daterange") {
      if (!this.fValue || this.fValue.from == null || this.fValue.to == null)
        return enumRuleErrorType.InvalidDateRange;

      const _boundNeedsValue = dateType =>
        !["today", "yesterday", "tomorrow", null].includes(dateType);

      const fromMissing =
        _boundNeedsValue(this.fValue.from.dateType) &&
        (this.fValue.from.value == null || this.fValue.from.value === "");
      const toMissing =
        _boundNeedsValue(this.fValue.to.dateType) &&
        (this.fValue.to.value == null || this.fValue.to.value === "");
      if (fromMissing && toMissing) return enumRuleErrorType.BothValuesEmpty;
      if (fromMissing || toMissing) return enumRuleErrorType.ValueEmpty;

      if (!validRange(this.fValue.from, this.fValue.to)) return enumRuleErrorType.InvalidDateRange;
    }

    if (this.fComparison == "range") {
      if (!Array.isArray(this.fValue) || this.fValue.length !== 2)
        return enumRuleErrorType.InvalidNumericRange;

      const startEmpty = this.fValue[0] == null || this.fValue[0] === "";
      const endEmpty = this.fValue[1] == null || this.fValue[1] === "";
      if (startEmpty && endEmpty) return enumRuleErrorType.BothValuesEmpty;
      if (startEmpty || endEmpty) return enumRuleErrorType.ValueEmpty;

      const v0 = Number(this.fValue[0]);
      const v1 = Number(this.fValue[1]);
      if (!Number.isFinite(v0) || !Number.isFinite(v1))
        return enumRuleErrorType.InvalidNumericRange;
      if (v0 > v1) return enumRuleErrorType.InvalidNumericRange;
    }

    return enumRuleErrorType.None;
  }

  /**
   * Returns the display label for a criteria item, e.g. "1", "2.1", "2.2".
   * @param {number} orBlockIndex
   * @param {number} orBlockLength
   * @param {number} ruleIndex
   * @returns {string}
   */
  static CriteriaLabel(orBlockIndex, orBlockLength, ruleIndex) {
    return orBlockLength > 1 ? `${orBlockIndex + 1}.${ruleIndex + 1}` : `${orBlockIndex + 1}`;
  }

  /**
   * Returns the criteria label, short error message, and composed display message
   * for the first invalid rule, or null if all rules are valid.
   * @param {QRulePair[]} rules
   * @returns {{ n: string, msg: string, message: string } | null}
   */
  static GetFirstInvalidCriteria(rules) {
    if (!Array.isArray(rules) || rules.length === 0) return null;
    const orBlocks = QRulePair.SplitIntoOrBlocks(rules, false);
    for (let i = 0; i < orBlocks.length; i++) {
      const block = orBlocks[i];
      for (let j = 0; j < block.length; j++) {
        const rule = block[j];
        const errorType = rule.GetErrorType();
        if (errorType !== enumRuleErrorType.None) {
          const n = QRulePair.CriteriaLabel(i, block.length, j);
          return {
            n,
            msg: QRulePair.GetMessageForErrorType(errorType),
            message: QRulePair.GetCriteriaMessage(errorType, n)
          };
        }
      }
    }
    return null;
  }

  /**
   * Returns the full composed validation message for a criteria error,
   * using a concise format for range errors and the standard wrapper for others.
   * @param {enumRuleErrorType} errorType
   * @param {string} n
   * @returns {string}
   */
  static GetCriteriaMessage(errorType, n) {
    switch (errorType) {
      case enumRuleErrorType.InvalidDateRange:
        return appstr("Invalid date range on criteria {n}", { n });
      case enumRuleErrorType.InvalidNumericRange:
        return appstr("Invalid range on criteria {n}", { n });
      case enumRuleErrorType.FieldEmpty:
      case enumRuleErrorType.ComparisonEmpty:
      case enumRuleErrorType.ValueEmpty:
      case enumRuleErrorType.BothValuesEmpty:
        return appstr("Missing required field on criteria {n}", { n });
      default:
        return appstr("Criteria {n} {msg}", {
          n,
          msg: QRulePair.GetMessageForErrorType(errorType)
        });
    }
  }

  /**
   * Returns the localized validation message for the given error type.
   * @param {enumRuleErrorType} errorType
   * @returns {string}
   */
  static GetMessageForErrorType(errorType) {
    switch (errorType) {
      case enumRuleErrorType.InvalidDateRange:
        return appstr("Invalid date range");
      case enumRuleErrorType.InvalidNumericRange:
        return appstr("Invalid numeric range");
      case enumRuleErrorType.FieldEmpty:
      case enumRuleErrorType.ComparisonEmpty:
      case enumRuleErrorType.ValueEmpty:
      case enumRuleErrorType.BothValuesEmpty:
        return appstr("Missing required field");
      default:
        return appstr("Generic default error");
    }
  }

  /**
   * Returns a short validation error message for the first invalid rule in the list,
   * or null if all rules are valid.
   * @param {QRulePair[]} rules
   * @returns {string | null}
   */
  static GetValidationMessage(rules) {
    if (!Array.isArray(rules)) return null;
    const invalid = rules.find(rule => !rule.IsValid());
    if (!invalid) return null;
    return QRulePair.GetMessageForErrorType(invalid.GetErrorType());
  }

  /**
   * Splits the given rule pairs list into OR blocks.
   * @param {QRulePair[]} ruleList
   * @param {boolean} [marshall=false] If true, the rules will be marshalled into MongoDB query format.
   * @returns {QRulePair[][]}
   */
  static SplitIntoOrBlocks(ruleList, marshall = false) {
    const orRuleBlocks = [];
    let currentOrRulesBlock = [];

    for (const rule of ruleList) {
      currentOrRulesBlock.push(marshall ? rule.Marshall() : rule);

      if (rule.fCondition === enumRulePairLogicCondition.and) {
        orRuleBlocks.push(currentOrRulesBlock);
        currentOrRulesBlock = [];
      }
    }

    if (currentOrRulesBlock.length) orRuleBlocks.push(currentOrRulesBlock);

    return orRuleBlocks;
  }
}
